// Team member "Edit access" — role, locations, permission toggles (ADDITIVE)
import React, { useEffect, useMemo, useState } from "react";
import { toast } from "./intelligence";

const API = process.env.REACT_APP_BACKEND_URL;
const call = async (p, init = {}) => {
  const r = await fetch(`${API}${p}`, { ...init, headers: { "Content-Type": "application/json", Authorization: "Bearer " + (localStorage.getItem("apex_auth_token") || "") } });
  if (!r.ok) { let m = ""; try { const d = await r.json(); m = typeof d.detail === "string" ? d.detail : JSON.stringify(d.detail); } catch {} throw new Error(m || `HTTP ${r.status}`); }
  return r.json();
};
const ROLES = ["Admin", "Estimator", "Coordinator", "Field Tech", "Accounting", "Marketing", "Salesperson", "Read-Only", "Custom"];
const overlay = { position: "fixed", inset: 0, background: "rgba(0,0,0,.45)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" };
const panel = { background: "var(--bg2, #fff)", color: "var(--text, #111)", border: "1px solid var(--border)", borderRadius: 8, width: 720, maxWidth: "94vw", maxHeight: "88vh", overflowY: "auto", padding: 22 };
const label = { fontSize: 10, letterSpacing: 1.5, textTransform: "uppercase", color: "var(--text3)", marginBottom: 4 };

function useAccessData(member) {
  const [catalog, setCatalog] = useState([]);
  const [locations, setLocations] = useState([]);
  const [customRoles, setCustomRoles] = useState([]);
  useEffect(() => {
    call("/api/iv/permissions/catalog").then((d) => { setCatalog(d.modules || []); }).catch(() => {});
    call("/api/workspaces").then((d) => setLocations(d.workspaces || [])).catch(() => {});
    call("/api/iv/roles/custom").then((d) => setCustomRoles(d.roles || [])).catch(() => {});
  }, [member?.id]);
  return { catalog, locations, customRoles };
}

export function EditAccessModal({ member, onClose, onSaved }) {
  const { catalog, locations, customRoles } = useAccessData(member);
  const [role, setRole] = useState(member.custom_role_id ? "Custom" : (ROLES.includes(member.system_role) ? member.system_role : "Read-Only"));
  const [customId, setCustomId] = useState(member.custom_role_id || "");
  const [locs, setLocs] = useState(member.location_ids?.length ? member.location_ids.map(String) : member.location_id ? [String(member.location_id)] : []);
  const [perms, setPerms] = useState(member.permissions_override || {});
  const [effective, setEffective] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const base = role === "Custom" ? "Read-Only" : role;
    call(`/api/iv/permissions/role/${encodeURIComponent(base)}`).then((d) => setEffective(d.permissions || {})).catch(() => setEffective({}));
  }, [role]);
  const isOn = (k) => (k in perms ? !!perms[k] : !!effective[k]);
  const toggle = (k) => setPerms((p) => ({ ...p, [k]: !isOn(k) }));
  const toggleLoc = (id) => setLocs((l) => (l.includes(id) ? l.filter((x) => x !== id) : [...l, id]));
  const changed = useMemo(() => Object.keys(perms).length, [perms]);

  const save = async () => {
    setSaving(true);
    try {
      await call(`/api/iv/team/${member.id}/access`, { method: "PUT", body: JSON.stringify({ system_role: role, custom_role_id: role === "Custom" ? customId : null, location_ids: locs, permissions_override: perms }) });
      toast("Access updated — applies on their next page load");
      onSaved && onSaved();
      onClose();
    } catch (e) { toast(e.message, "err"); }
    setSaving(false);
  };

  return (
    <div style={overlay} data-testid="edit-access-modal" onClick={onClose}>
      <div style={panel} onClick={(e) => e.stopPropagation()}>
        <div style={{ fontFamily: "var(--rajdhani)", fontSize: 18, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1 }}>Edit access</div>
        <div style={{ fontSize: 12, color: "var(--text3)", marginBottom: 16 }}>{member.full_name} · {member.email}</div>
        <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginBottom: 16 }}>
          <div>
            <div style={label}>Role</div>
            <select data-testid="edit-access-role" className="form-control" value={role} onChange={(e) => { setRole(e.target.value); setPerms({}); }}>
              {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
          {role === "Custom" && (
            <div>
              <div style={label}>Custom role</div>
              <select data-testid="edit-access-custom-role" className="form-control" value={customId} onChange={(e) => setCustomId(e.target.value)}>
                <option value="">Select…</option>
                {customRoles.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
              </select>
            </div>
          )}
          <div style={{ flex: 1, minWidth: 260 }}>
            <div style={label}>Location(s)</div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }} data-testid="edit-access-locations">
              {locations.map((l) => (
                <label key={l.id} style={{ fontSize: 12, display: "flex", alignItems: "center", gap: 4 }}>
                  <input type="checkbox" data-testid={`edit-access-loc-${l.id}`} checked={locs.includes(String(l.id))} onChange={() => toggleLoc(String(l.id))} /> {l.name}
                </label>
              ))}
            </div>
            <div style={{ fontSize: 10, color: "var(--text3)", marginTop: 4 }}>First checked location is their primary location.</div>
          </div>
        </div>
        <div style={label}>Permissions {changed ? `· ${changed} custom change(s)` : ""}</div>
        <div style={{ border: "1px solid var(--border)", borderRadius: 6, padding: 10, maxHeight: 340, overflowY: "auto" }} data-testid="edit-access-permissions">
          {catalog.map((mod) => (
            <div key={mod.module} style={{ marginBottom: 10 }}>
              <div style={{ fontSize: 11, fontWeight: 700, marginBottom: 4 }}>{mod.module}</div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(210px,1fr))", gap: 4 }}>
                {mod.items.filter((it) => !it.owner_only).map((it) => (
                  <label key={it.key} style={{ fontSize: 11, display: "flex", gap: 6, alignItems: "center" }}>
                    <input type="checkbox" data-testid={`edit-access-perm-${it.key}`} checked={isOn(it.key)} onChange={() => toggle(it.key)} /> {it.label}
                  </label>
                ))}
              </div>
            </div>
          ))}
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 16 }}>
          <button className="btn btn-ghost" onClick={onClose} data-testid="edit-access-cancel">Cancel</button>
          <button className="btn btn-primary" disabled={saving || !locs.length || (role === "Custom" && !customId)} onClick={save} data-testid="edit-access-save">{saving ? "Saving…" : "Save"}</button>
        </div>
      </div>
    </div>
  );
}
